"use client";

import * as React from "react";

import type { InteractiveCarouselProps } from "@/components/ui/InteractiveCarousel";
import useDeferredLoad from "@/lib/hooks/useDeferredLoad";
import { cn } from "@/lib/utils/cn";

interface LazyTourCardsCarouselProps {
  carouselClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  childWrapperClassName?: string;
  children: React.ReactNode;
  isGhostPage?: boolean;
  isCarouselDesktop?: boolean;
}

type InteractiveCarouselComponent =
  React.ComponentType<InteractiveCarouselProps>;

function FallbackCarousel({
  children,
  className,
  trackClassName,
  itemClassName,
}: {
  children: React.ReactNode;
  className?: string;
  trackClassName?: string;
  itemClassName?: string;
}) {
  const items = React.Children.toArray(children);

  return (
    <div className={cn("max-w-full", className)}>
      <div className={cn("flex items-stretch", trackClassName)}>
        {items.map((child, index) => (
          <div key={index} className={cn("flex shrink-0", itemClassName)}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LazyTourCardsCarousel({
  carouselClassName,
  contentClassName,
  itemClassName,
  childWrapperClassName,
  children,
  isGhostPage = false,
  isCarouselDesktop = false,
}: LazyTourCardsCarouselProps) {
  const [InteractiveCarousel, setInteractiveCarousel] =
    React.useState<InteractiveCarouselComponent | null>(null);
  const wrappedChildren = React.Children.toArray(children).map(
    (child, index) => (
      <div key={index} className={cn("h-full w-full", childWrapperClassName)}>
        {child}
      </div>
    ),
  );
  const { ref, shouldLoad } = useDeferredLoad({
    rootMargin: "250px 0px",
    idleMs: 1200,
  });

  React.useEffect(() => {
    if (!shouldLoad || InteractiveCarousel) return;

    let isActive = true;

    import("@/components/ui/InteractiveCarousel").then((module) => {
      if (isActive) {
        setInteractiveCarousel(() => module.default);
      }
    });

    return () => {
      isActive = false;
    };
  }, [InteractiveCarousel, shouldLoad]);

  const desktopArrowClassName = isGhostPage
    ? "bg-ghost-red text-white"
    : "bg-primary-blue text-white";

  return (
    <div ref={ref}>
      {InteractiveCarousel ? (
        <>
          <InteractiveCarousel
            className={cn("max-w-full lg:hidden", carouselClassName)}
            opts={{ align: "start", containScroll: "trimSnaps" }}
            contentClassName={cn(
              "w-full max-w-full items-stretch",
              contentClassName,
            )}
            itemClassName={cn("flex h-auto", itemClassName)}
            showDots
          >
            {wrappedChildren}
          </InteractiveCarousel>

          {isCarouselDesktop ? (
            <InteractiveCarousel
              className="hidden select-none lg:block"
              contentClassName="-ml-4 w-full max-w-full items-stretch"
              itemClassName="flex basis-1/3 h-auto pl-6"
              showArrows
              nextClassName={desktopArrowClassName}
              previousClassName={desktopArrowClassName}
            >
              {wrappedChildren}
            </InteractiveCarousel>
          ) : null}
        </>
      ) : (
        <>
          <FallbackCarousel
            className="lg:hidden"
            trackClassName="snap-x snap-mandatory overflow-x-auto pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            itemClassName="min-w-full snap-start"
          >
            {wrappedChildren}
          </FallbackCarousel>

          {isCarouselDesktop ? (
            <FallbackCarousel
              className="hidden lg:block"
              trackClassName="snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              itemClassName="min-w-[calc((100%-3rem)/3)] snap-start"
            >
              {wrappedChildren}
            </FallbackCarousel>
          ) : null}
        </>
      )}
    </div>
  );
}
